<template>
  <DashboardLayout>

    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Appraisal</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Memos (Admin)</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Memos (Admin)</h1>
                <p class="mb-0 small">View and manage memos</p>
              </div>
            </div>

            <div class="form-body">
              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">MDA:</label>
                    <SearchableSelect v-model="filters.mda" :options="uniqueMDAs" placeholder="All" small clearable />
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Initiated:</label>
                    <select class="form-select form-select-sm" v-model="filters.date">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status">
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="returned">Returned</option>
                      <option value="appraisal initiated">Appraisal Initiated</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="errorMessage" class="alert alert-warning alert-dismissible fade show" role="alert">
                {{ errorMessage }}
                <button type="button" class="btn-close" aria-label="Close" @click.prevent="clearError()"></button>
              </div>

              <!-- Table Controls -->
              <div class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 500px;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search by org code or amount approved..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>File Title</th>
                        <th>File Number</th>
                        <th>Creation Date</th>
                        <th>Originating MDA</th>
                        <th class="text-end">Amount Approved</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-if="loading">
                        <tr v-for="n in pageLength" :key="'skeleton-' + n" class="skeleton-row">
                          <td><div class="skeleton-bar skeleton-sm mx-auto"></div></td>
                          <td><div class="skeleton-bar"></div></td>
                          <td><div class="skeleton-bar"></div></td>
                          <td><div class="skeleton-bar"></div></td>
                          <td><div class="skeleton-bar"></div></td>
                          <td><div class="skeleton-bar"></div></td>
                          <td><div class="skeleton-bar skeleton-sm"></div></td>
                        </tr>
                      </template>

                      <template v-else>
                        <tr v-if="paginatedItems.length === 0">
                          <td :colspan="7" class="text-center text-muted py-4">
                            <div v-if="tableNotice">{{ tableNotice }}</div>
                            <div v-else>No memos to display.</div>
                          </td>
                        </tr>
                        <tr v-for="(memo, index) in paginatedItems" :key="memo.id"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td data-label="S/N" class="serial-no text-center">
                            <span>{{ (currentPage - 1) * pageLength + index + 1 }}</span>
                          </td>
                          <td data-label="File Title" class="primary-col" title="View Memo"
                            @click.prevent="viewMemo(memo.id)">
                            <router-link
                              :to="{ name: 'ViewMemo', params: { id: memo.id }, query: { mdaid: memo.mdaId, year: memo.year } }"
                              class="view-link text-decoration-underline" @mouseenter="showTooltip($event, memo)"
                              @mouseleave="hideTooltip">
                              {{ memo.fileTitle }}
                            </router-link>
                          </td>
                          <td data-label="File Number" class="secondary-col">{{ memo.fileNumber }}</td>
                          <td data-label="Creation Date" class="secondary-col memo-date">{{ formatDate(memo.createdDate)
                          }}</td>
                          <td data-label="Originating MDA" class="secondary-col">{{ memo.originatingMda }}</td>
                          <td data-label="Amount Approved (₦)" class="secondary-col amount">
                            {{ formatCurrency(memo.amountApproved || 0).toLocaleString() }}
                          </td>
                          <td data-label="Status" class="secondary-col">{{ getMemoStatus(memo) }}</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Tooltip -->
              <div v-if="tooltipVisible && hoveredNotification" class="custom-tooltip" :style="{
                top: tooltipY + 'px',
                left: tooltipX + 'px'
              }">
                <div v-if="isLoadingDetails">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading details...
                </div>
                <div v-else>
                  <div class="tooltip-header">
                    <p><strong>Details</strong></p>
                  </div>                  
                  <!-- MEMO -->
                  <div>
                    <p><strong>File Title: </strong>{{ hoveredNotification.fileTitle || 'N/A' }}</p>
                    <p><strong>File Number: </strong>{{ hoveredNotification.fileNumber || 'N/A' }}</p>
                    <p v-if="hoveredNotification.pendingApproval"><strong>Old MDA: </strong>{{ hoveredNotification.oldOriginatingMda || 'N/A' }}</p>
                    <p><strong>MDA: </strong>{{ hoveredNotification.originatingMda || 'N/A' }}</p>
                    <p v-if="hoveredNotification.pendingApproval"><strong>Old Amount Approved: </strong>{{ formatCurrency(hoveredNotification.oldAmountApproved || 0) }}</p>
                    <p><strong>Amount Approved: </strong>{{ formatCurrency(hoveredNotification.amountApproved || 0) }}</p>
                    <p><strong>Org Code: </strong>{{ hoveredNotification.orgCode || 'N/A' }}</p>
                  </div>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, totalElements) }} of
                  {{ totalElements }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0" :class="{ 'pe-none opacity-50': loading }">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import Cookies from 'js-cookie';
import DashboardLayout from '../layout/DashboardLayout.vue';
import SearchableSelect from '../common/SearchableSelect.vue';

import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css' // Import global responsive styles

const router = useRouter()
const route = useRoute()
const mdas = ref([])
const loading = ref(true)
const isLoadingDetails = ref(false)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const hoveredNotification = ref(null)
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)

const filters = ref({
  mda: '',
  date: '',
  status: ''
})
const errorMessage = ref('')
const tableNotice = ref('')

// Server-side pagination: `items` only ever holds the currently cached
// batch, `batchStart` is the global 0-indexed offset of its first item, and
// `totalElements` is the backend's totalElements.
const items = ref([])
const batchStart = ref(0)
const totalElements = ref(0)
const initialized = ref(false)

// Always fetch the backend's max page size (100), regardless of the UI's
// `pageLength`, so smaller page sizes (10/25/50) stay cached across more
// pages instead of triggering a network call on every page turn.
const FETCH_SIZE = 100

// Which backend search param the current search term actually matched
// against (see resolveSearch below), and the value sent for it.
const activeSearchField = ref(null)
const activeSearchValue = ref(null)

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

// Drops the cached batch and re-fetches page 1 — used whenever something
// that changes what the backend returns (mdaId, search field) changes,
// since the old cache no longer applies.
const resetAndRefetch = async () => {
  currentPage.value = 1
  items.value = []
  batchStart.value = 0
  totalElements.value = 0
  initialized.value = false
  await ensureBatchForPage(1)
}

const showTooltip = async (event, memo) => {
  hoveredNotification.value = memo
  tooltipVisible.value = true

  const rect = event.currentTarget.getBoundingClientRect()

  tooltipX.value = rect.left + window.scrollX
  tooltipY.value = rect.bottom + window.scrollY + 8 // small offset

  await fetchMemoDetails(memo.id)
}

const hideTooltip = () => {
  tooltipVisible.value = false
}

const fetchMemoDetails = async (id) => {
  const token = Cookies.get('auth_token');
  isLoadingDetails.value = true
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/memo/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    hoveredNotification.value = {
      ...hoveredNotification.value,
      ...data
    }
  } catch (error) {
    console.error('Error fetching Memo:', error);
  } finally {
    isLoadingDetails.value = false
  }
};

watch(pageLength, resetAndRefetch)

// mdaId is filtered server-side, so a change requires a fresh fetch. `date`
// and `status` have no backend support here (status depends on a
// client-side cross-check against appraisals — see fetchMemoBatch) and stay
// client-side filters applied only to whichever batch is currently cached.
watch(() => filters.value.mda, resetAndRefetch)
watch(() => filters.value.date, () => {
  currentPage.value = 1
})
watch(() => filters.value.status, () => {
  currentPage.value = 1
})

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatCurrency = (amount) => {
  return '₦' + Number(amount || 0).toLocaleString('en-NG')
}

const getMemoStatus = (memo) => {
  if (memo.hasAppraisal) {
    return 'Appraisal Initiated'
  }

  if (memo.isRejected) return 'Rejected'
  if (memo.isReturned) return 'Returned'

  return 'Pending'
}

const fetchMDAs = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    mdas.value = response.data.map(mda => ({
      id: mda.id,
      name: mda.name,
      organisation_code: mda.orgCode,
    }))
  } catch (error) {
    console.error('Failed to fetch MDAs:', error)
  }
}

const uniqueMDAs = computed(() => {
  return [...mdas.value].sort((a, b) => a.name.localeCompare(b.name))
})

const mapMemo = (memo) => ({
  id: memo.id,
  fileTitle: memo.fileTitle,
  fileNumber: memo.fileNumber,
  createdDate: memo.createdDate,
  sector: memo.sectorId,
  originatingMda: memo.originatingMda,
  amountApproved: memo.amountApproved,
  purposeOfRequest: memo.purposeOfRequest,
  isEditable: memo.isEditableInd,
  isRejected: memo.memoRejectedInd,
  isReturned: memo.memoReturnedInd,
  mdaId: memo.mdaId,
  year: memo.year,
})

// Builds the backend query params for the currently active MDA filter and
// search field/value.
const buildParams = () => {
  const params = {}
  if (filters.value.mda) params.mdaId = filters.value.mda
  if (activeSearchField.value) params[activeSearchField.value] = activeSearchValue.value
  return params
}

// Fetches one batch of `size` rows starting at backend page `backendPage`
// (0-indexed), caching it locally. Whether a memo already has an appraisal
// isn't a stored field — it's only knowable by cross-checking the
// appraisal service per file number — so that check runs once per memo in
// the fetched batch, same as before pagination was added, just scoped to
// the batch instead of the whole dataset.
const fetchMemoBatch = async (backendPage, size, extraParams = {}) => {
  const token = Cookies.get('auth_token')

  try {
    const response = await axios.get(`${root_api_url}/appraisal/memo/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: { page: backendPage, size, ...extraParams },
    })

    const data = response.data
    if (!data || !Array.isArray(data.content)) {
      items.value = []
      batchStart.value = backendPage * size
      totalElements.value = 0
      return false
    }

    const memosWithStatus = await Promise.all(
      data.content.map(async (memo) => {
        let hasAppraisal = false
        try {
          const appraisalResponse = await axios.get(`${root_api_url}/appraisal/appraisal/memo/${memo.fileNumber}`)
          hasAppraisal = !!appraisalResponse.data
        } catch (error) {
          hasAppraisal = false
        }

        return { ...mapMemo(memo), hasAppraisal }
      })
    )

    items.value = memosWithStatus
    batchStart.value = backendPage * size
    totalElements.value = data.totalElements
    return items.value.length > 0
  } catch (error) {
    console.error('Failed to fetch memo batch:', error)
    items.value = []
    totalElements.value = 0
    return false
  }
}

// Makes sure the cached batch covers the UI page's global row range,
// fetching only when it doesn't already have it cached.
const ensureBatchForPage = async (uiPage) => {
  const pageLen = pageLength.value
  const globalStart = (uiPage - 1) * pageLen
  const globalEnd = globalStart + pageLen - 1
  const params = buildParams()

  if (!initialized.value) {
    loading.value = true
    errorMessage.value = ''
    tableNotice.value = ''
    try {
      const found = await fetchMemoBatch(0, FETCH_SIZE, params)
      initialized.value = true
      if (!found) {
        tableNotice.value = 'No memos found.'
      }
    } finally {
      loading.value = false
    }
    return
  }

  const cacheEnd = batchStart.value + items.value.length - 1
  const isCached = items.value.length > 0 && globalStart >= batchStart.value && globalEnd <= cacheEnd
  if (isCached) return

  loading.value = true
  try {
    const backendPage = Math.floor(globalStart / FETCH_SIZE)
    await fetchMemoBatch(backendPage, FETCH_SIZE, params)
  } finally {
    loading.value = false
  }
}

// A single search box can't OR across orgCode/amountApproved in one backend
// call (the endpoint combines whichever params are passed, it doesn't match
// any-of), so probe each candidate field in turn and keep whichever one
// actually returns results.
const SEARCH_FIELDS = ['orgCode']

const resolveSearch = async () => {
  const term = debouncedSearch.value
  currentPage.value = 1
  items.value = []
  batchStart.value = 0
  totalElements.value = 0
  initialized.value = false
  errorMessage.value = ''

  if (!term) {
    activeSearchField.value = null
    activeSearchValue.value = null
    await ensureBatchForPage(1)
    return
  }

  const attempts = SEARCH_FIELDS.map(field => ({ field, value: term }))
  // Strip currency/thousands formatting (e.g. "₦5,000" from formatCurrency)
  // before parsing, so amount searches work with values copied off the table.
  const cleanedTerm = term.replace(/[^0-9.]/g, '')
  const numericTerm = Number(cleanedTerm)
  if (cleanedTerm !== '' && !Number.isNaN(numericTerm)) {
    attempts.push({ field: 'amountApproved', value: numericTerm })
  }

  loading.value = true
  try {
    for (const attempt of attempts) {
      activeSearchField.value = attempt.field
      activeSearchValue.value = attempt.value
      // eslint-disable-next-line no-await-in-loop
      const found = await fetchMemoBatch(0, FETCH_SIZE, buildParams())
      initialized.value = true
      if (found) {
        tableNotice.value = ''
        return
      }
    }

    // Nothing matched on any field.
    activeSearchField.value = null
    activeSearchValue.value = null
    tableNotice.value = 'No memos found matching your search.'
  } finally {
    loading.value = false
  }
}

watch(debouncedSearch, resolveSearch)

// Applies the client-side-only date/status filters (and recency sort) to a
// list of currently-cached memo rows.
const applyClientFilters = (rows) => {
  let filtered = rows

  if (filters.value.date) {
    const now = new Date()
    filtered = filtered.filter(w => {
      const createdDate = new Date(w.createdDate)
      switch (filters.value.date) {
        case 'this_month':
          return createdDate.getMonth() === now.getMonth() &&
            createdDate.getFullYear() === now.getFullYear()
        case 'last_month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
          return createdDate.getMonth() === lastMonth.getMonth() &&
            createdDate.getFullYear() === lastMonth.getFullYear()
        }
        case 'this_year':
          return createdDate.getFullYear() === now.getFullYear()
        default:
          return true
      }
    })
  }

  if (filters.value.status) {
    filtered = filtered.filter(w => getMemoStatus(w).toLowerCase() === filters.value.status.toLowerCase())
  }

  return [...filtered].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
}

// All currently cached rows, with the client-side-only filters applied.
// Used for the export buttons, which — like the display below — only cover
// whatever's presently cached, not the full backend dataset.
const filteredCachedItems = computed(() => applyClientFilters(items.value))

// Composes the display rows for the current UI page from the cached batch,
// then applies the same client-side filters scoped to just this page's
// slice.
const paginatedItems = computed(() => {
  const globalStart = (currentPage.value - 1) * pageLength.value
  const globalEnd = globalStart + pageLength.value - 1
  const localStart = globalStart - batchStart.value
  const localEnd = Math.min(globalEnd, totalElements.value - 1) - batchStart.value
  const rows = items.value.slice(localStart, localEnd + 1)

  return applyClientFilters(rows)
})

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(totalElements.value / pageLength.value))
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = async (page) => {
  if (page < 1 || page > totalPages.value) return
  await ensureBatchForPage(page)
  currentPage.value = page
}

const clearFilters = () => {
  filters.value = {
    mda: '',
    date: '',
    status: ''
  }
  search.value = ''
  activeSearchField.value = null
  activeSearchValue.value = null
  resetAndRefetch()
}

const viewMemo = (memoId) => {
  if (!memoId) {
    errorMessage.value = 'Unable to open memo details: memo ID is missing.'
    return
  }
  router.push({ name: 'ViewMemo', params: { id: memoId } })
}

const clearError = () => {
  errorMessage.value = ''
}

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'File Title', 'File Number', 'Created Date', 'Originating MDA', 'Amount Approved (₦)', 'Purpose of Request', 'Status']],
    body: filteredCachedItems.value.map((memo, index) => [
      index + 1,
      memo.fileTitle,
      memo.fileNumber,
      memo.createdDate,
      memo.originatingMda,
      memo.amountApproved,
      memo.purposeOfRequest,
      getMemoStatus(memo)
    ])
  })
  doc.save('Memos.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('memos')
  worksheet.addRow(['S/N', 'File Title', 'File Number', 'Created Date', 'Originating MDA', 'Amount Approved (₦)', 'Purpose of Request', 'Status'])

  filteredCachedItems.value.forEach((memo, index) => {
    worksheet.addRow([
      index + 1,
      memo.fileTitle,
      memo.fileNumber,
      memo.createdDate,
      memo.originatingMda,
      memo.amountApproved,
      memo.purposeOfRequest,
      getMemoStatus(memo)
    ])
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'memos.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  // MDAs don't depend on the memo batch, so let that load on its own
  // timeline instead of waiting behind the memo fetch.
  fetchMDAs()

  if (route.query.status) {
    filters.value.status = route.query.status
  }
  await ensureBatchForPage(1)
  document.title = "View Admin Memos"
});

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.memo-date {
  min-width: 150px;
}

.primary-col {
  font-weight: 500;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.skeleton-row td {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}

.skeleton-bar {
  height: 14px;
  width: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #eceff1 25%, #f5f6f7 37%, #eceff1 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.skeleton-bar.skeleton-sm {
  width: 24px;
}

.skeleton-bar.skeleton-lg {
  width: 90%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}

.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background: #ffffff;
  color: #000;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 280px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  animation: fadeIn 0.15s ease-in-out;
}

.custom-tooltip p {
  margin: 0 0 6px;
}

.custom-tooltip p:last-child {
  margin-bottom: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
